/// <reference types="gtag.js" />
import { FLAGS } from '@iheartjane/feature-flags';
import { CartAnalyticsEvent, NavigationEvent } from '@iheartjane/menu';
import { useCallback, useState } from 'react';
import { useFetcher, useNavigate, useSearchParams } from 'react-router';

import type { CartActionResult } from '@jane/bloom/actions/cart.server';
import { usePinStore } from '@jane/bloom/hooks/storeSwitcher/usePinStore';
import { useHardRedirect } from '@jane/bloom/hooks/useHardRedirect';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { useAuthModal } from '@jane/bloom/providers/authModalProvider';
import { useBrandSpecialShortfallNotice } from '@jane/bloom/providers/brandSpecialShortfallProvider';
import { CartAction } from '@jane/bloom/routes/_menu.checkout._index/types';
import {
  addToCartPayload,
  addToCartQuantityPayload,
  beginCheckoutPayload,
  purchasePayload,
  removeFromCartPayload,
} from '@jane/bloom/services/tracking/googleAnalytics/ecommerce';
import type { IframeMessageEvent } from '@jane/bloom/services/tracking/iframeMessageEvent';
import { clearPromoCode } from '@jane/bloom/utils/cookies/clearPromoCode';
import { queryStringContaining } from '@jane/bloom/utils/queryParams/preservingQueryParams';

/**
 * Trace references to postMessageToIframeParent
 * @see https://docs.iheartjane.com/jane-docs/implementing-roots/embedding-checkout#client-side-event-callbacks
 */
export const HeadlessCartEvents = {
  ...CartAnalyticsEvent,
  ...NavigationEvent,
  HeadlessAppLoaded: 'headlessAppLoaded',
  InitiateExternalAuth: 'initiateExternalAuth',
} as const;

export const useHandleCartEvents = (cartStoreId?: string) => {
  const remixNavigate = useNavigate();
  const hardRedirect = useHardRedirect();
  const [searchParams] = useSearchParams();
  const { pinStore } = usePinStore();
  const {
    cookiePrefix,
    featureFlags: { [FLAGS.obGtagEvents]: gtagEventsEnabled },
    shoppingPreferences,
  } = useRootLoaderData();
  const { openAuthModal } = useAuthModal();
  const fetcher = useFetcher<CartActionResult>();
  useBrandSpecialShortfallNotice(fetcher.data);
  const [loaded, setLoaded] = useState(false);

  const handleCartEvents = useCallback(
    ({ data }: IframeMessageEvent) => {
      switch (data?.payload?.name) {
        case HeadlessCartEvents.HeadlessAppLoaded: {
          setLoaded(true);
          break;
        }
        case HeadlessCartEvents.CartPageLoad: {
          const ecommerce = beginCheckoutPayload({
            ...data.payload.properties,
            storeId: data.payload.properties.storeId ?? cartStoreId,
          });
          window.dataLayer?.push({
            event: 'begin_checkout',
            ...data.payload.properties,
            ecommerce,
          });
          if (gtagEventsEnabled) {
            window.gtag?.('event', 'begin_checkout', ecommerce);
          }
          break;
        }
        case HeadlessCartEvents.InitiateExternalAuth: {
          const skipBag = data.payload?.source === 'checkout';
          openAuthModal({
            onSuccess: ({ redirectTo }) => hardRedirect(redirectTo),
            skipBagAfterAuth: skipBag,
          });
          break;
        }
        case HeadlessCartEvents.CartItemAdd: {
          fetcher.submit(
            {
              intent: CartAction.RefreshCart,
            },
            { method: 'post' }
          );

          const ecommerce = addToCartPayload({
            ...data.payload.properties,
            storeId: data.payload.properties.storeId ?? cartStoreId,
          });
          window.dataLayer?.push({
            event: 'add_to_cart',
            ...data.payload.properties,
            ecommerce,
            products: ecommerce.items,
          });
          if (gtagEventsEnabled) {
            window.gtag?.('event', 'add_to_cart', ecommerce);
          }

          break;
        }
        case HeadlessCartEvents.CartItemChangeCount: {
          fetcher.submit(
            {
              intent: CartAction.RefreshCart,
            },
            { method: 'post' }
          );

          window.dataLayer?.push({
            event: 'change_cart_item_count',
            ...data.payload.properties,
          });

          if (data.payload.properties) {
            const {
              count,
              discountedPrice,
              previousCount,
              price,
              priceId,
              productId,
            } = data.payload.properties;
            if (previousCount != null && count < previousCount) {
              const ecommerce = removeFromCartPayload({
                discountedPrice,
                price,
                priceId,
                productId,
                quantity: previousCount - count,
                storeId: cartStoreId,
              });
              window.dataLayer?.push({
                event: 'remove_from_cart',
                ...data.payload.properties,
                ecommerce,
              });
              if (gtagEventsEnabled) {
                window.gtag?.('event', 'remove_from_cart', ecommerce);
              }
            } else if (previousCount != null && count > previousCount) {
              const ecommerce = addToCartQuantityPayload({
                discountedPrice,
                price,
                priceId,
                productId,
                quantity: count - previousCount,
                storeId: cartStoreId,
              });
              window.dataLayer?.push({
                event: 'add_to_cart',
                ...data.payload.properties,
                ecommerce,
              });
              if (gtagEventsEnabled) {
                window.gtag?.('event', 'add_to_cart', ecommerce);
              }
            }
          }

          break;
        }
        case HeadlessCartEvents.CartItemRemoval: {
          fetcher.submit(
            {
              intent: CartAction.RefreshCart,
            },
            { method: 'post' }
          );

          const ecommerce = removeFromCartPayload({
            ...data.payload.properties,
            storeId: data.payload.properties.storeId ?? cartStoreId,
          });
          window.dataLayer?.push({
            event: 'remove_from_cart',
            ...data.payload.properties,
            ecommerce,
          });
          if (gtagEventsEnabled) {
            window.gtag?.('event', 'remove_from_cart', ecommerce);
          }

          break;
        }
        case HeadlessCartEvents.Checkout: {
          fetcher.submit(
            {
              intent: CartAction.ClearCart,
              uuid: data?.payload?.properties?.uuid,
            },
            { method: 'post' }
          );

          // Clear promo code cookie after successful checkout
          clearPromoCode(cookiePrefix);

          const ecommerce = purchasePayload({
            ...data.payload.properties,
            storeId: data.payload.properties.storeId ?? cartStoreId,
          });
          window.dataLayer?.push({
            event: 'purchase',
            ...data.payload.properties,
            ecommerce,
          });
          if (gtagEventsEnabled) {
            window.gtag?.('event', 'purchase', ecommerce);
          }

          break;
        }
        case HeadlessCartEvents.ChangeStore: {
          const preservedParams = queryStringContaining({
            prefix: '&',
            searchParams,
          });
          remixNavigate(`/featured?select-store=true${preservedParams}`);
          break;
        }
        case HeadlessCartEvents.DrawerContinueShopping: {
          const queryParams = queryStringContaining({
            searchParams,
          });
          pinStore({
            redirectTo: `/featured${queryParams}`,
            reservationMode: shoppingPreferences?.reservationMode ?? 'pickup',
            storeId: cartStoreId ? Number(cartStoreId) : undefined,
          });
          break;
        }
        case HeadlessCartEvents.DrawerClosed: {
          remixNavigate(-1);
          break;
        }
        case HeadlessCartEvents.NavigateToBrandSpecial: {
          remixNavigate(data.payload.link);
          break;
        }
        case HeadlessCartEvents.NavigateToProduct: {
          remixNavigate(data.payload.link);
          break;
        }
        default: {
          break;
        }
      }
    },
    [
      cartStoreId,
      cookiePrefix,
      fetcher,
      gtagEventsEnabled,
      hardRedirect,
      openAuthModal,
      pinStore,
      remixNavigate,
      searchParams,
      setLoaded,
      shoppingPreferences,
    ]
  );

  return {
    handleCartEvents,
    loaded,
  };
};
